import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Landmark,
  House,
  Trash2,
  Check,
  X,
  FileCheck2,
  FileClock,
  FileText,
  Download,
  LoaderCircle,
  RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";

import "./LoanCasesPage.css";

import CreateLoanCaseModal from "./application/loan-case/CreateLoanCaseModal";
import { useToast } from "../../../components/feedback/ToastProvider";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   HELPERS
   ========================================================= */

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "—";
  }

  return new Intl.NumberFormat("en-EU", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(numericValue);
}

function formatFileSize(bytes) {
  const numericValue = Number(bytes);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  if (numericValue < 1024) {
    return `${numericValue} B`;
  }

  if (numericValue < 1024 * 1024) {
    return `${(numericValue / 1024).toFixed(1)} KB`;
  }

  return `${(numericValue / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocumentDate(value) {
  if (!value) {
    return "";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
}

function getPageNumbers(currentPage, totalPages) {
  const pages = [];

  const maxVisible = 4;

  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push(i);
    }

    return pages;
  }

  let start = Math.max(1, currentPage - 1);

  let end = Math.min(totalPages, start + maxVisible - 1);

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i += 1) {
    pages.push(i);
  }

  return pages;
}

function humanizeEnum(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .trim()
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word, index) => {
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }

      return word;
    })
    .join(" ");
}

function getAdditionalLoans(loanCase) {
  return Array.isArray(loanCase?.additional_loans)
    ? loanCase.additional_loans
    : [];
}

function getAdditionalLoanTotal(loanCase) {
  const backendTotal = Number(loanCase?.additional_loan_total);

  if (Number.isFinite(backendTotal)) {
    return backendTotal;
  }

  return getAdditionalLoans(loanCase).reduce((total, additionalLoan) => {
    const amount = Number(
      additionalLoan?.approved_renovation_loan_amount ??
        additionalLoan?.approved_total_loan_amount ??
        0,
    );

    return total + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

function getCombinedLoanAmount(loanCase) {
  const backendCombined = Number(loanCase?.combined_loan_amount);

  if (Number.isFinite(backendCombined)) {
    return backendCombined;
  }

  const initialAmount = Number(loanCase?.loan_amount);
  const safeInitialAmount = Number.isFinite(initialAmount) ? initialAmount : 0;

  return safeInitialAmount + getAdditionalLoanTotal(loanCase);
}

/* =========================================================
   MANUAL ENTRY
   ========================================================= */

const MANUAL_STEP_PATHS = {
  PERSONAL: "personal-info",
  FINANCIAL: "financial-info",
  DOCUMENTS: "documents",
  PROPERTY: "property-info",
  FINAL_LOAN: "final-loan-details",
  REVIEW: "review-save",
};

function getManualEntryTarget(loanCase) {
  const id = loanCase?.loan_case_id ?? loanCase?.id;

  if (!id) {
    console.error(
      "Cannot create manual-entry path: missing loan-case ID.",
      loanCase,
    );

    return null;
  }

  const currentStep = loanCase?.manual_entry_current_step || "PERSONAL";

  const stepPath = MANUAL_STEP_PATHS[currentStep] || "personal-info";

  return `/lender/loan-cases/${id}` + `/manual-entry/${stepPath}`;
}

/* =========================================================
   RENOVATION PLAN VIEWER
   ========================================================= */

function RenovationPlanViewerModal({ plan, onClose }) {
  const { t } = useTranslation("lender-loan-cases");

  const { getAccessTokenSilently } = useAuth0();

  const [previewUrl, setPreviewUrl] = React.useState("");

  const [isLoading, setIsLoading] = React.useState(true);

  const [isDownloading, setIsDownloading] = React.useState(false);

  const [error, setError] = React.useState("");

  const previewUrlRef = React.useRef("");

  const fileEndpoint = resolveRenovationPlanFileEndpoint(plan);

  /* =======================================================
     BODY LOCK + ESCAPE
     ======================================================= */

  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose?.();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  /* =======================================================
     CLEANUP
     ======================================================= */

  React.useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  /* =======================================================
     LOAD FILE
     ======================================================= */

  React.useEffect(() => {
    if (!fileEndpoint) {
      setIsLoading(false);

      setError(
        t(
          "renovationPlan.errors.notAvailable",
          "The renovation plan file is not available.",
        ),
      );

      return;
    }

    const controller = new AbortController();

    async function loadPlan() {
      try {
        setIsLoading(true);
        setError("");

        const token = await getAccessTokenSilently();

        const response = await fetch(`${fileEndpoint}?disposition=inline`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },

          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));

          throw new Error(
            data?.detail ||
              t(
                "renovationPlan.errors.loadFailed",
                "The renovation plan could not be loaded.",
              ),
          );
        }

        const blob = await response.blob();

        const objectUrl = URL.createObjectURL(blob);

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }

        previewUrlRef.current = objectUrl;

        setPreviewUrl(objectUrl);
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }

        console.error("Failed to load renovation plan:", err);

        setError(
          err?.message ||
            t(
              "renovationPlan.errors.loadFailed",
              "The renovation plan could not be loaded.",
            ),
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    loadPlan();

    return () => {
      controller.abort();
    };
  }, [fileEndpoint, getAccessTokenSilently, t]);

  /* =======================================================
     DOWNLOAD
     ======================================================= */

  async function handleDownload() {
    if (!fileEndpoint || isDownloading) {
      return;
    }

    try {
      setIsDownloading(true);
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(`${fileEndpoint}?disposition=attachment`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "renovationPlan.errors.downloadFailed",
              "The renovation plan could not be downloaded.",
            ),
        );
      }

      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");

      anchor.href = objectUrl;

      anchor.download = plan?.filename || "renovation-plan.pdf";

      document.body.appendChild(anchor);

      anchor.click();

      anchor.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Failed to download renovation plan:", err);

      setError(
        err?.message ||
          t(
            "renovationPlan.errors.downloadFailed",
            "The renovation plan could not be downloaded.",
          ),
      );
    } finally {
      setIsDownloading(false);
    }
  }

  const mimeType = plan?.mime_type || "";

  const isImage = mimeType.startsWith("image/");

  return (
    <div
      className="loan-cases-plan-modal__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        className="loan-cases-plan-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="loan-cases-plan-modal-title"
      >
        {/* ===============================================
            HEADER
            =============================================== */}

        <div className="loan-cases-plan-modal__header">
          <div className="loan-cases-plan-modal__heading">
            <div className="loan-cases-plan-modal__heading-icon">
              <FileText size={21} />
            </div>

            <div className="loan-cases-plan-modal__heading-copy">
              <h2
                id="loan-cases-plan-modal-title"
                className="loan-cases-plan-modal__title"
              >
                {t("renovationPlan.modal.title", "Renovation plan")}
              </h2>

              <p className="loan-cases-plan-modal__filename">
                {plan?.filename ||
                  t("renovationPlan.modal.file", "Renovation plan document")}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="loan-cases-plan-modal__close"
            onClick={onClose}
            aria-label={t("renovationPlan.modal.close", "Close")}
          >
            <X size={19} />
          </button>
        </div>

        {/* ===============================================
            META
            =============================================== */}

        <div className="loan-cases-plan-modal__meta">
          {plan?.project_code ? (
            <span className="loan-cases-plan-modal__meta-pill">
              {t("renovationPlan.modal.project", "Project")}:{" "}
              <strong>{plan.project_code}</strong>
            </span>
          ) : null}

          {plan?.size_bytes ? (
            <span className="loan-cases-plan-modal__meta-pill">
              {formatFileSize(plan.size_bytes)}
            </span>
          ) : null}

          {plan?.uploaded_at ? (
            <span className="loan-cases-plan-modal__meta-pill">
              {t("renovationPlan.modal.uploaded", "Uploaded")}:{" "}
              {formatDocumentDate(plan.uploaded_at)}
            </span>
          ) : null}
        </div>

        {/* ===============================================
            BODY
            =============================================== */}

        <div className="loan-cases-plan-modal__body">
          {isLoading ? (
            <div className="loan-cases-plan-modal__state">
              <LoaderCircle
                size={28}
                className="loan-cases-plan-modal__spinner"
              />

              <span>
                {t(
                  "renovationPlan.modal.loading",
                  "Loading renovation plan...",
                )}
              </span>
            </div>
          ) : error ? (
            <div className="loan-cases-plan-modal__state loan-cases-plan-modal__state--error">
              <FileText size={28} />

              <span>{error}</span>
            </div>
          ) : previewUrl ? (
            isImage ? (
              <div className="loan-cases-plan-modal__image-wrap">
                <img
                  src={previewUrl}
                  alt={plan?.filename || "Renovation plan"}
                  className="loan-cases-plan-modal__image"
                />
              </div>
            ) : (
              <iframe
                src={previewUrl}
                title={plan?.filename || "Renovation plan"}
                className="loan-cases-plan-modal__frame"
              />
            )
          ) : null}
        </div>

        {/* ===============================================
            FOOTER
            =============================================== */}

        <div className="loan-cases-plan-modal__footer">
          <div className="loan-cases-plan-modal__footer-copy">
            {t(
              "renovationPlan.modal.helper",
              "Use the renovation plan to review and confirm the renovation financing amount.",
            )}
          </div>

          <button
            type="button"
            className="loan-cases-plan-modal__download"
            onClick={handleDownload}
            disabled={isDownloading || !fileEndpoint}
          >
            {isDownloading ? (
              <LoaderCircle
                size={16}
                className="loan-cases-plan-modal__spinner"
              />
            ) : (
              <Download size={16} />
            )}

            <span>
              {isDownloading
                ? t("renovationPlan.modal.downloading", "Downloading...")
                : t("renovationPlan.modal.download", "Download")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   RENOVATION OUTCOME MODAL
   ========================================================= */

function RenovationOutcomeModal({ renovation, onClose }) {
  const { t } = useTranslation("lender-loan-cases");

  const { getAccessTokenSilently } = useAuth0();

  const outcome = renovation?.outcome || null;

  const finalEpc = outcome?.final_epc || null;

  const finalEpcEndpoint = resolveFinalEpcFileEndpoint(finalEpc);

  const [isDownloading, setIsDownloading] = React.useState(false);

  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);

  const [previewUrl, setPreviewUrl] = React.useState("");

  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

  const [fileError, setFileError] = React.useState("");

  const previewUrlRef = React.useRef("");

  /* =======================================================
     BODY LOCK + ESCAPE
     ======================================================= */

  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        if (isPreviewOpen) {
          setIsPreviewOpen(false);

          return;
        }

        onClose?.();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPreviewOpen, onClose]);

  /* =======================================================
     OBJECT URL CLEANUP
     ======================================================= */

  React.useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  /* =======================================================
     FINAL EPC VIEW
     ======================================================= */

  async function handleViewFinalEpc() {
    if (!finalEpcEndpoint || isLoadingPreview) {
      return;
    }

    try {
      setIsLoadingPreview(true);
      setFileError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(`${finalEpcEndpoint}?disposition=inline`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "renovationOutcome.errors.epcLoadFailed",
              "The final EPC document could not be loaded.",
            ),
        );
      }

      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);

      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }

      previewUrlRef.current = objectUrl;

      setPreviewUrl(objectUrl);
      setIsPreviewOpen(true);
    } catch (err) {
      console.error("Failed to load final EPC:", err);

      setFileError(
        err?.message ||
          t(
            "renovationOutcome.errors.epcLoadFailed",
            "The final EPC document could not be loaded.",
          ),
      );
    } finally {
      setIsLoadingPreview(false);
    }
  }

  /* =======================================================
     FINAL EPC DOWNLOAD
     ======================================================= */

  async function handleDownloadFinalEpc() {
    if (!finalEpcEndpoint || isDownloading) {
      return;
    }

    try {
      setIsDownloading(true);
      setFileError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${finalEpcEndpoint}?disposition=attachment`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "renovationOutcome.errors.epcDownloadFailed",
              "The final EPC document could not be downloaded.",
            ),
        );
      }

      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");

      anchor.href = objectUrl;

      anchor.download = finalEpc?.filename || "final-epc.pdf";

      document.body.appendChild(anchor);

      anchor.click();

      anchor.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Failed to download final EPC:", err);

      setFileError(
        err?.message ||
          t(
            "renovationOutcome.errors.epcDownloadFailed",
            "The final EPC document could not be downloaded.",
          ),
      );
    } finally {
      setIsDownloading(false);
    }
  }

  /* =======================================================
     DERIVED
     ======================================================= */

  const plannedRenovation = renovation?.renovation || {};

  const isCompleted = renovation?.project_status === "COMPLETED";

  const finalEpcMimeType = finalEpc?.mime_type || "";

  const finalEpcIsImage = finalEpcMimeType.startsWith("image/");

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div
      className="loan-cases-outcome-modal__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        className="loan-cases-outcome-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="loan-cases-outcome-modal-title"
      >
        {/* ===============================================
            HEADER
            =============================================== */}

        <div className="loan-cases-outcome-modal__header">
          <div className="loan-cases-outcome-modal__heading">
            <div className="loan-cases-outcome-modal__heading-icon">
              <FileCheck2 size={22} />
            </div>

            <div>
              <span className="loan-cases-outcome-modal__eyebrow">
                {t("renovationOutcome.modal.eyebrow", "RENOVATION OUTCOME")}
              </span>

              <h2
                id="loan-cases-outcome-modal-title"
                className="loan-cases-outcome-modal__title"
              >
                {t("renovationOutcome.modal.title", "Renovation outcome")}
              </h2>

              <p className="loan-cases-outcome-modal__description">
                {t(
                  "renovationOutcome.modal.description",
                  "Review the actual renovation results reported by the Homebuyer for the external renovation project.",
                )}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="loan-cases-outcome-modal__close"
            onClick={onClose}
            aria-label={t("renovationOutcome.modal.close", "Close")}
          >
            <X size={19} />
          </button>
        </div>

        {/* ===============================================
            BODY
            =============================================== */}

        <div className="loan-cases-outcome-modal__body">
          {/* STATUS */}

          <div className="loan-cases-outcome-modal__status-row">
            <div>
              <span className="loan-cases-outcome-modal__status-label">
                {t("renovationOutcome.modal.status", "Project status")}
              </span>

              <span
                className={`loan-cases-outcome-modal__status ${
                  isCompleted
                    ? "loan-cases-outcome-modal__status--completed"
                    : "loan-cases-outcome-modal__status--progress"
                }`}
              >
                {isCompleted
                  ? t("renovationOutcome.modal.completedStatus", "Completed")
                  : t(
                      "renovationOutcome.modal.inProgressStatus",
                      "In progress",
                    )}
              </span>
            </div>

            {renovation?.external_oss?.name ? (
              <div className="loan-cases-outcome-modal__source">
                <span>
                  {t("renovationOutcome.modal.externalOss", "External OSS")}
                </span>
                <strong>{renovation.external_oss.name}</strong>
              </div>
            ) : null}
          </div>

          {/* =============================================
              PLANNED VS ACTUAL
              ============================================= */}

          <section className="loan-cases-outcome-modal__section">
            <div className="loan-cases-outcome-modal__section-heading">
              <span>
                {t(
                  "renovationOutcome.modal.plannedActual",
                  "Planned and actual renovation",
                )}
              </span>
            </div>

            <div className="loan-cases-outcome-modal__comparison-grid">
              <div className="loan-cases-outcome-modal__metric">
                <span>
                  {t(
                    "renovationOutcome.modal.plannedCost",
                    "Planned renovation cost",
                  )}
                </span>

                <strong>
                  {formatCurrency(plannedRenovation?.planned_cost)}
                </strong>
              </div>

              <div className="loan-cases-outcome-modal__metric loan-cases-outcome-modal__metric--actual">
                <span>
                  {t(
                    "renovationOutcome.modal.actualCost",
                    "Actual renovation cost",
                  )}
                </span>

                <strong>
                  {formatCurrency(outcome?.actual_renovation_cost)}
                </strong>
              </div>

              <div className="loan-cases-outcome-modal__metric">
                <span>
                  {t("renovationOutcome.modal.expectedStart", "Expected start")}
                </span>

                <strong>
                  {formatDocumentDate(plannedRenovation?.expected_start_date) ||
                    "—"}
                </strong>
              </div>

              <div className="loan-cases-outcome-modal__metric loan-cases-outcome-modal__metric--actual">
                <span>
                  {t("renovationOutcome.modal.actualStart", "Actual start")}
                </span>

                <strong>
                  {formatDocumentDate(outcome?.actual_start_date) || "—"}
                </strong>
              </div>

              <div className="loan-cases-outcome-modal__metric">
                <span>
                  {t(
                    "renovationOutcome.modal.expectedCompletion",
                    "Expected completion",
                  )}
                </span>

                <strong>
                  {formatDocumentDate(
                    plannedRenovation?.expected_completion_date,
                  ) || "—"}
                </strong>
              </div>

              <div className="loan-cases-outcome-modal__metric loan-cases-outcome-modal__metric--actual">
                <span>
                  {t(
                    "renovationOutcome.modal.actualCompletion",
                    "Actual completion",
                  )}
                </span>

                <strong>
                  {formatDocumentDate(outcome?.actual_completion_date) || "—"}
                </strong>
              </div>
            </div>
          </section>

          {/* =============================================
              COMPLETED WORKS
              ============================================= */}

          <section className="loan-cases-outcome-modal__section">
            <div className="loan-cases-outcome-modal__section-heading">
              <span>
                {t("renovationOutcome.modal.completedWorks", "Completed works")}
              </span>
            </div>

            <div className="loan-cases-outcome-modal__text-card">
              {outcome?.completed_works_summary ||
                t(
                  "renovationOutcome.modal.noCompletedWorks",
                  "No completed works summary has been provided.",
                )}
            </div>
          </section>

          {/* =============================================
              FINAL ENERGY PERFORMANCE
              ============================================= */}

          <section className="loan-cases-outcome-modal__section">
            <div className="loan-cases-outcome-modal__section-heading">
              <span>
                {t(
                  "renovationOutcome.modal.energyPerformance",
                  "Final energy performance",
                )}
              </span>
            </div>

            <div className="loan-cases-outcome-modal__epc-panel">
              <div className="loan-cases-outcome-modal__epc-primary">
                <div>
                  <span>
                    {t(
                      "renovationOutcome.modal.finalEpcLabel",
                      "Final EPC label",
                    )}
                  </span>

                  <strong className="loan-cases-outcome-modal__epc-label">
                    {outcome?.final_epc_label || "—"}
                  </strong>
                </div>

                <div>
                  <span>
                    {t(
                      "renovationOutcome.modal.finalEpcScore",
                      "Final EPC energy score",
                    )}
                  </span>

                  <strong>
                    {outcome?.final_epc_energy_score
                      ? `${outcome.final_epc_energy_score} kWh/m²/year`
                      : "—"}
                  </strong>
                </div>
              </div>

              <div className="loan-cases-outcome-modal__epc-details">
                <div>
                  <span>
                    {t(
                      "renovationOutcome.modal.certificateNumber",
                      "Certificate number",
                    )}
                  </span>

                  <strong>
                    {outcome?.final_epc_certificate_number || "—"}
                  </strong>
                </div>

                <div>
                  <span>
                    {t("renovationOutcome.modal.issueDate", "Issue date")}
                  </span>

                  <strong>
                    {formatDocumentDate(outcome?.final_epc_issue_date) || "—"}
                  </strong>
                </div>

                <div>
                  <span>
                    {t(
                      "renovationOutcome.modal.energySavings",
                      "Estimated energy savings",
                    )}
                  </span>

                  <strong>
                    {formatOutcomePercent(
                      outcome?.estimated_energy_savings_percent,
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    {t(
                      "renovationOutcome.modal.co2Reduction",
                      "Estimated CO₂ reduction",
                    )}
                  </span>

                  <strong>
                    {formatOutcomePercent(
                      outcome?.estimated_co2_reduction_percent,
                    )}
                  </strong>
                </div>
              </div>
            </div>
          </section>

          {/* =============================================
              NOTES
              ============================================= */}

          {outcome?.completion_notes ? (
            <section className="loan-cases-outcome-modal__section">
              <div className="loan-cases-outcome-modal__section-heading">
                <span>
                  {t(
                    "renovationOutcome.modal.completionNotes",
                    "Completion notes",
                  )}
                </span>
              </div>

              <div className="loan-cases-outcome-modal__text-card">
                {outcome.completion_notes}
              </div>
            </section>
          ) : null}

          {/* =============================================
              FINAL EPC DOCUMENT
              ============================================= */}

          <section className="loan-cases-outcome-modal__section">
            <div className="loan-cases-outcome-modal__section-heading">
              <span>
                {t(
                  "renovationOutcome.modal.finalEpcDocument",
                  "Final EPC certificate",
                )}
              </span>
            </div>

            {finalEpc && finalEpcEndpoint ? (
              <div className="loan-cases-outcome-modal__document-card">
                <div className="loan-cases-outcome-modal__document-icon">
                  <FileText size={22} />
                </div>

                <div className="loan-cases-outcome-modal__document-copy">
                  <strong>
                    {finalEpc.filename ||
                      t(
                        "renovationOutcome.modal.file",
                        "Final EPC certificate",
                      )}
                  </strong>

                  <span>
                    {[
                      formatFileSize(finalEpc.size_bytes),

                      finalEpc.uploaded_at
                        ? formatDocumentDate(finalEpc.uploaded_at)
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>

                <div className="loan-cases-outcome-modal__document-actions">
                  <button
                    type="button"
                    className="loan-cases-outcome-modal__document-btn"
                    onClick={handleViewFinalEpc}
                    disabled={isLoadingPreview}
                  >
                    {isLoadingPreview ? (
                      <LoaderCircle
                        size={15}
                        className="loan-cases-plan-modal__spinner"
                      />
                    ) : (
                      <FileText size={15} />
                    )}

                    <span>{t("renovationOutcome.modal.view", "View")}</span>
                  </button>

                  <button
                    type="button"
                    className="loan-cases-outcome-modal__document-btn loan-cases-outcome-modal__document-btn--primary"
                    onClick={handleDownloadFinalEpc}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <LoaderCircle
                        size={15}
                        className="loan-cases-plan-modal__spinner"
                      />
                    ) : (
                      <Download size={15} />
                    )}

                    <span>
                      {isDownloading
                        ? t(
                            "renovationOutcome.modal.downloading",
                            "Downloading...",
                          )
                        : t("renovationOutcome.modal.download", "Download")}
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="loan-cases-outcome-modal__document-missing">
                <FileClock size={18} />

                <span>
                  {t(
                    "renovationOutcome.modal.finalEpcMissing",
                    "Final EPC certificate has not been uploaded yet.",
                  )}
                </span>
              </div>
            )}

            {fileError ? (
              <div className="loan-cases-outcome-modal__file-error">
                {fileError}
              </div>
            ) : null}
          </section>

          {/* =============================================
              INLINE EPC PREVIEW
              ============================================= */}

          {isPreviewOpen && previewUrl ? (
            <section className="loan-cases-outcome-modal__preview-section">
              <div className="loan-cases-outcome-modal__preview-header">
                <div>
                  <span>
                    {t(
                      "renovationOutcome.modal.finalEpcDocument",
                      "Final EPC certificate",
                    )}
                  </span>

                  <strong>{finalEpc?.filename || "Final EPC"}</strong>
                </div>

                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  aria-label={t("renovationOutcome.modal.close", "Close")}
                >
                  <X size={17} />
                </button>
              </div>

              <div className="loan-cases-outcome-modal__preview-body">
                {finalEpcIsImage ? (
                  <div className="loan-cases-outcome-modal__preview-image-wrap">
                    <img
                      src={previewUrl}
                      alt={finalEpc?.filename || "Final EPC"}
                      className="loan-cases-outcome-modal__preview-image"
                    />
                  </div>
                ) : (
                  <iframe
                    src={previewUrl}
                    title={finalEpc?.filename || "Final EPC"}
                    className="loan-cases-outcome-modal__preview-frame"
                  />
                )}
              </div>
            </section>
          ) : null}
        </div>

        {/* ===============================================
            FOOTER
            =============================================== */}

        <div className="loan-cases-outcome-modal__footer">
          <p>
            {t(
              "renovationOutcome.modal.helper",
              "The renovation outcome is provided by the Homebuyer for a renovation managed outside the LEG-UP One-Stop Shop network.",
            )}
          </p>

          <button
            type="button"
            className="loan-cases-outcome-modal__close-btn"
            onClick={onClose}
          >
            {t("renovationOutcome.modal.close", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
}

function resolveRenovationPlanFileEndpoint(plan) {
  if (!plan) {
    return "";
  }

  if (plan.file_url) {
    if (/^https?:\/\//i.test(plan.file_url)) {
      return plan.file_url;
    }

    return `${API_BASE}${
      plan.file_url.startsWith("/") ? "" : "/"
    }${plan.file_url}`;
  }

  if (plan.project_id && plan.document_id) {
    return (
      `${API_BASE}` +
      `/api/projects/` +
      `${plan.project_id}` +
      `/documents/` +
      `${plan.document_id}` +
      `/file/`
    );
  }

  return "";
}

function resolveFinalEpcFileEndpoint(finalEpc) {
  if (!finalEpc?.file_url) {
    return "";
  }

  if (/^https?:\/\//i.test(finalEpc.file_url)) {
    return finalEpc.file_url;
  }

  return `${API_BASE}${
    finalEpc.file_url.startsWith("/") ? "" : "/"
  }${finalEpc.file_url}`;
}

function formatOutcomePercent(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "—";
  }

  return `${numericValue.toLocaleString("en-GB", {
    maximumFractionDigits: 2,
  })}%`;
}

function getAvailableRenovationPlan(loanCase) {
  const internalPlan = loanCase?.renovation_plan || null;

  if (internalPlan && resolveRenovationPlanFileEndpoint(internalPlan)) {
    return {
      ...internalPlan,
      source: internalPlan.source || "LEGUP_OSS",
    };
  }

  const externalPlan = loanCase?.external_renovation?.plan || null;

  if (externalPlan && resolveRenovationPlanFileEndpoint(externalPlan)) {
    return {
      ...externalPlan,
      source: "EXTERNAL_OSS",
    };
  }

  return null;
}

function getExternalRenovationOutcome(loanCase) {
  return loanCase?.external_renovation?.outcome || null;
}

function hasExternalRenovationOutcome(loanCase) {
  return Boolean(
    loanCase?.external_renovation?.has_outcome ||
    getExternalRenovationOutcome(loanCase)?.available,
  );
}

function hasExternalFinalEpc(loanCase) {
  return Boolean(getExternalRenovationOutcome(loanCase)?.has_final_epc);
}

function externalRenovationIsCompleted(loanCase) {
  return loanCase?.external_renovation?.project_status === "COMPLETED";
}

function hasExternalRenovation(loanCase) {
  return Boolean(loanCase?.external_renovation?.available);
}

/* =========================================================
   PAGE
   ========================================================= */

export default function LoancasesPage() {
  const { t } = useTranslation("lender-loan-cases");

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const { showToast } = useToast();

  const navigate = useNavigate();

  const { marketRegions = [] } = useOutletContext();

  const [selectedMarketRegionId, setSelectedMarketRegionId] =
    React.useState("");

  React.useEffect(() => {
    if (marketRegions.length === 1 && !selectedMarketRegionId) {
      setSelectedMarketRegionId(String(marketRegions[0].id));
    }
  }, [marketRegions, selectedMarketRegionId]);

  /* =======================================================
     FILTERS / PAGINATION
     ======================================================= */

  const [statusFilter, setStatusFilter] = React.useState("all");

  const [sourceFilter, setSourceFilter] = React.useState("all");

  const [pageSize, setPageSize] = React.useState(6);

  const [searchTerm, setSearchTerm] = React.useState("");

  const [searchInput, setSearchInput] = React.useState("");

  const [currentPage, setCurrentPage] = React.useState(1);

  /* =======================================================
     DATA
     ======================================================= */

  const [loancases, setLoancases] = React.useState([]);

  const [totalLoancases, setTotalLoancases] = React.useState(0);

  const [totalPages, setTotalPages] = React.useState(1);

  /* =======================================================
     RENOVATION PLAN & OUTCOME
     ======================================================= */

  const [selectedRenovationPlan, setSelectedRenovationPlan] =
    React.useState(null);

  const [selectedRenovationOutcome, setSelectedRenovationOutcome] =
    React.useState(null);

  /* =======================================================
     MANUAL CASE CREATION
     ======================================================= */

  const [isCreatingManual, setIsCreatingManual] = React.useState(false);

  const [manualCreateError, setManualCreateError] = React.useState("");

  /* =======================================================
     LOADING
     ======================================================= */

  const [isInitialLoading, setIsInitialLoading] = React.useState(true);

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const [error, setError] = React.useState("");

  /* =======================================================
     CREATE MODAL
     ======================================================= */

  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);

  const [isCreateModalClosing, setIsCreateModalClosing] = React.useState(false);

  /* =======================================================
     RELOAD
     ======================================================= */

  const [reloadKey, setReloadKey] = React.useState(0);

  const hasLoadedOnceRef = React.useRef(false);

  /* =======================================================
     SELECTION / DELETE
     ======================================================= */

  const [isSelectionMode, setIsSelectionMode] = React.useState(false);

  const [selectedLoanCaseIds, setSelectedLoanCaseIds] = React.useState([]);

  const [deleteTargetIds, setDeleteTargetIds] = React.useState([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const [isDeleting, setIsDeleting] = React.useState(false);

  const [deleteError, setDeleteError] = React.useState("");

  /* =======================================================
     CREATE MODAL
     ======================================================= */

  function closeCreateModal() {
    setIsCreateModalClosing(true);

    window.setTimeout(() => {
      setIsCreateModalOpen(false);

      setIsCreateModalClosing(false);
    }, 220);
  }

  async function handleCreateManualLoanCase() {
    if (isCreatingManual) {
      return;
    }

    if (!selectedMarketRegionId) {
      setManualCreateError(
        t(
          "errors.marketRegionRequired",
          "Please select a market region before creating the loan case.",
        ),
      );

      return;
    }

    try {
      setIsCreatingManual(true);
      setManualCreateError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/lender/loan-cases/manual/`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            case_type: "INITIAL",

            market_region_id: Number(selectedMarketRegionId),
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          data?.detail ||
          data?.errors?.detail ||
          data?.market_region_id?.[0] ||
          data?.case_type?.[0] ||
          t(
            "errors.manualCreateFailed",
            "The manual loan case could not be created.",
          );

        throw new Error(message);
      }

      const createdLoanCaseId = data?.loan_case_id ?? data?.id;

      if (!createdLoanCaseId) {
        throw new Error(
          "The backend created the case but did not return a loan_case_id.",
        );
      }

      const target = getManualEntryTarget({
        id: createdLoanCaseId,

        manual_entry_current_step:
          data?.manual_entry_current_step || "PERSONAL",
      });

      navigate(target, {
        state: {
          dossierId: data?.dossier_id || null,

          caseReference: data?.case_reference || "",

          currentStep: data?.manual_entry_current_step || "PERSONAL",

          marketRegionId: Number(selectedMarketRegionId),

          isNewManualCase: true,
        },
      });
    } catch (err) {
      console.error("Failed to create manual loan case:", err);

      setManualCreateError(
        err?.message ||
          t(
            "errors.manualCreateFailed",
            "The manual loan case could not be created.",
          ),
      );
    } finally {
      setIsCreatingManual(false);
    }
  }

  /* =======================================================
     SEARCH
     ======================================================= */

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchTerm(searchInput.trim());

      setCurrentPage(1);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, sourceFilter, pageSize]);

  /* =======================================================
     LOAD
     ======================================================= */

  React.useEffect(() => {
    if (!isAuthenticated) {
      setLoancases([]);
      setTotalLoancases(0);
      setTotalPages(1);
      setError("");
      setIsInitialLoading(false);
      setIsRefreshing(false);

      return;
    }

    const controller = new AbortController();

    async function loadLoancases() {
      const hasExistingData = hasLoadedOnceRef.current;

      try {
        if (hasExistingData) {
          setIsRefreshing(true);
        } else {
          setIsInitialLoading(true);
        }

        setError("");

        const token = await getAccessTokenSilently();

        const params = new URLSearchParams({
          page: String(currentPage),

          page_size: String(pageSize),

          status: statusFilter,

          source: sourceFilter,
        });

        if (searchTerm) {
          params.set("search", searchTerm);
        }

        const response = await fetch(
          `${API_BASE}/api/lender/loan-cases/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },

            signal: controller.signal,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (!hasExistingData) {
            setLoancases([]);
            setTotalLoancases(0);
            setTotalPages(1);

            setError(t("errors.loadFailed"));
          }

          return;
        }

        setLoancases(Array.isArray(data.results) ? data.results : []);

        setTotalLoancases(
          Number.isFinite(data.count) ? data.count : data.results?.length || 0,
        );

        setTotalPages(
          Number.isFinite(data.total_pages) && data.total_pages > 0
            ? data.total_pages
            : 1,
        );

        hasLoadedOnceRef.current = true;

        setError("");
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }

        console.error("Failed to load lender loan cases", err);

        if (!hasExistingData) {
          setLoancases([]);
          setTotalLoancases(0);
          setTotalPages(1);

          setError(t("errors.loadFailed"));
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsInitialLoading(false);

          setIsRefreshing(false);
        }
      }
    }

    loadLoancases();

    return () => controller.abort();
  }, [
    currentPage,
    getAccessTokenSilently,
    isAuthenticated,
    pageSize,
    reloadKey,
    searchTerm,
    statusFilter,
    sourceFilter,
    t,
  ]);

  /* =======================================================
     CASE HELPERS
     ======================================================= */

  function isManualCase(loanCase) {
    return loanCase.source === "MANUAL_ENTRY";
  }

  function isReferenceCase(loanCase) {
    return loanCase.source === "REFERENCE_IMPORT";
  }

  function isManualEntryCompleted(loanCase) {
    return Boolean(loanCase.manual_entry_completed);
  }

  function hasFinalDecision(loanCase) {
    return Boolean(
      loanCase.has_result || loanCase.status === "RESULT_RECORDED",
    );
  }

  function hasDraftDecision(loanCase) {
    return Boolean(loanCase.has_draft_result || loanCase.is_draft_result);
  }

  function isApprovedDecision(loanCase) {
    return Boolean(
      hasFinalDecision(loanCase) && loanCase.decision === "APPROVED",
    );
  }

  function isRejectedDecision(loanCase) {
    return Boolean(
      hasFinalDecision(loanCase) && loanCase.decision === "REJECTED",
    );
  }

  function canContinueManualEntry(loanCase) {
    return Boolean(isManualCase(loanCase) && !isManualEntryCompleted(loanCase));
  }

  function hasExistingLenderDecision(loanCase) {
    if (isManualCase(loanCase)) {
      return isManualEntryCompleted(loanCase);
    }

    return Boolean(hasDraftDecision(loanCase) || hasFinalDecision(loanCase));
  }

  function canOpenDecision(loanCase) {
    if (isReferenceCase(loanCase)) {
      return true;
    }

    if (isManualCase(loanCase)) {
      return isManualEntryCompleted(loanCase);
    }

    return false;
  }

  function canAddAdditionalLoan(loanCase) {
    if (typeof loanCase?.can_add_additional_loan === "boolean") {
      return loanCase.can_add_additional_loan;
    }

    return Boolean(
      loanCase?.case_type === "INITIAL" &&
      canOpenDecision(loanCase) &&
      isApprovedDecision(loanCase) &&
      loanCase?.renovation_financing_status === "CONFIRMED",
    );
  }

  /* =======================================================
     RENOVATION PLAN HELPERS
     ======================================================= */

  function hasRenovationPlan(loanCase) {
    return Boolean(getAvailableRenovationPlan(loanCase));
  }

  function renovationFinancingIsEstimated(loanCase) {
    return loanCase?.renovation_financing_status === "ESTIMATED";
  }

  function renovationFinancingIsConfirmed(loanCase) {
    return loanCase?.renovation_financing_status === "CONFIRMED";
  }

  function shouldReviewRenovationFinancing(loanCase) {
    return Boolean(
      hasRenovationPlan(loanCase) &&
      isApprovedDecision(loanCase) &&
      renovationFinancingIsEstimated(loanCase),
    );
  }

  /* =======================================================
     NAVIGATION
     ======================================================= */

  function getLoanCaseTarget(loanCase) {
    const id = loanCase.loan_case_id || loanCase.id;

    if (canContinueManualEntry(loanCase)) {
      return getManualEntryTarget(loanCase);
    }

    if (canOpenDecision(loanCase)) {
      return `/lender/loan-cases/${id}` + "/submit-decision";
    }

    return null;
  }

  const openLoanCase = React.useCallback(
    (loanCase) => {
      const target = getLoanCaseTarget(loanCase);

      if (target) {
        navigate(target);
      }
    },
    [navigate],
  );

  const recordLoanCaseResult = React.useCallback(
    (loanCase) => {
      const id = loanCase.loan_case_id || loanCase.id;

      navigate(`/lender/loan-cases/${id}/submit-decision`);
    },
    [navigate],
  );

  React.useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [currentPage]);

  /* =======================================================
     PAGINATION
     ======================================================= */

  const visiblePages = getPageNumbers(currentPage, totalPages);

  const startIndex =
    totalLoancases === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const endIndex = Math.min(currentPage * pageSize, totalLoancases);

  const showFullError = !isInitialLoading && !!error && loancases.length === 0;

  const showEmpty =
    !isInitialLoading &&
    !error &&
    loancases.length === 0 &&
    totalLoancases === 0;

  const showRows = loancases.length > 0;

  /* =======================================================
     DISPLAY HELPERS
     ======================================================= */

  function getInitials(name) {
    const parts = String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!parts.length) {
      return "—";
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return (`${parts[0][0]}` + `${parts[parts.length - 1][0]}`).toUpperCase();
  }

  function getSourceLabel(source) {
    if (source === "REFERENCE_IMPORT") {
      return t("filters.source.reference");
    }

    if (source === "MANUAL_ENTRY") {
      return t("filters.source.manual");
    }

    return source || "—";
  }

  function getCaseTypeLabel(caseType) {
    const labels = {
      INITIAL: t("caseTypes.initial", "Initial loan"),

      ADDITIONAL: t("caseTypes.additional", "Additional loan"),

      REFINANCING: t("caseTypes.refinancing", "Refinancing"),
    };

    return labels[caseType] || humanizeEnum(caseType);
  }

  function getPropertyTypeLabel(propertyType) {
    const labels = {
      STUDIO_APARTMENT: t("propertyTypes.studioApartment", "Studio apartment"),

      APARTMENT: t("propertyTypes.apartment", "Apartment"),

      FLAT: t("propertyTypes.flat", "Flat"),

      DUPLEX: t("propertyTypes.duplex", "Duplex"),

      PENTHOUSE: t("propertyTypes.penthouse", "Penthouse"),

      DETACHED_HOUSE: t("propertyTypes.detachedHouse", "Detached house"),

      SEMI_DETACHED_HOUSE: t(
        "propertyTypes.semiDetachedHouse",
        "Semi-detached house",
      ),

      TERRACED_HOUSE: t("propertyTypes.terracedHouse", "Terraced house"),

      TOWNHOUSE: t("propertyTypes.townhouse", "Townhouse"),

      SINGLE_FAMILY_HOUSE: t(
        "propertyTypes.singleFamilyHouse",
        "Single-family house",
      ),

      MULTI_FAMILY_HOUSE: t(
        "propertyTypes.multiFamilyHouse",
        "Multi-family house",
      ),

      BUNGALOW: t("propertyTypes.bungalow", "Bungalow"),

      VILLA: t("propertyTypes.villa", "Villa"),

      LOFT: t("propertyTypes.loft", "Loft"),

      MAISONETTE: t("propertyTypes.maisonette", "Maisonette"),

      OTHER: t("propertyTypes.other", "Other property type"),
    };

    return labels[propertyType] || humanizeEnum(propertyType);
  }

  function getDecisionLabel(loanCase) {
    if (hasDraftDecision(loanCase)) {
      return t("decisions.draft", "Draft decision");
    }

    if (isManualCase(loanCase) && isManualEntryCompleted(loanCase)) {
      if (loanCase.decision === "REJECTED") {
        return t("decisions.rejected", "Rejected");
      }

      return t("decisions.approved", "Approved");
    }

    if (isApprovedDecision(loanCase)) {
      return t("decisions.approved", "Approved");
    }

    if (isRejectedDecision(loanCase)) {
      return t("decisions.rejected", "Rejected");
    }

    if (hasFinalDecision(loanCase)) {
      return t("decisions.recorded", "Decision recorded");
    }

    return t("decisions.pending", "Decision pending");
  }

  function getDecisionActionLabel(loanCase) {
    if (hasDraftDecision(loanCase)) {
      return t("row.continueDecision", "Continue decision");
    }

    if (isManualCase(loanCase) && isManualEntryCompleted(loanCase)) {
      return t("row.reviewUpdateDecision", "Review / update decision");
    }

    if (isApprovedDecision(loanCase)) {
      return t("row.reviewUpdateDecision", "Review / update decision");
    }

    if (isRejectedDecision(loanCase)) {
      return t("row.viewDecision", "View decision");
    }

    return t("row.submitDecision", "Submit decision");
  }

  /* =======================================================
     AVATAR
     ======================================================= */

  function ApplicantAvatar({ loanCase }) {
    const [imageFailed, setImageFailed] = React.useState(false);

    const showImage = loanCase.primary_applicant_image_url && !imageFailed;

    return (
      <div className="loan-cases-page__avatar">
        {showImage ? (
          <img
            src={loanCase.primary_applicant_image_url}
            alt={loanCase.primary_applicant_name || "Applicant"}
            className="loan-cases-page__avatar-img"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span className="loan-cases-page__avatar-initials">
            {getInitials(loanCase.primary_applicant_name)}
          </span>
        )}
      </div>
    );
  }

  /* =======================================================
     SELECTION
     ======================================================= */

  const visibleLoanCaseIds = React.useMemo(
    () =>
      loancases
        .map((loanCase) => loanCase.loan_case_id || loanCase.id)
        .filter(Boolean),
    [loancases],
  );

  const allVisibleSelected =
    visibleLoanCaseIds.length > 0 &&
    visibleLoanCaseIds.every((id) => selectedLoanCaseIds.includes(id));

  function enterSelectionMode() {
    setSelectedLoanCaseIds([]);

    setIsSelectionMode(true);
  }

  function exitSelectionMode() {
    setSelectedLoanCaseIds([]);

    setIsSelectionMode(false);
  }

  function toggleLoanCaseSelection(id) {
    setSelectedLoanCaseIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  }

  function toggleVisibleLoanCases() {
    setSelectedLoanCaseIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleLoanCaseIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleLoanCaseIds]));
    });
  }

  /* =======================================================
     DELETE
     ======================================================= */

  function openDeleteModal(ids) {
    const normalizedIds = Array.from(new Set(ids.filter(Boolean)));

    if (!normalizedIds.length) {
      return;
    }

    setDeleteTargetIds(normalizedIds);

    setDeleteError("");

    setIsDeleteModalOpen(true);
  }

  function closeDeleteModal() {
    if (isDeleting) {
      return;
    }

    setIsDeleteModalOpen(false);

    setDeleteTargetIds([]);

    setDeleteError("");
  }

  async function confirmDeleteLoanCases() {
    if (!deleteTargetIds.length || isDeleting) {
      return;
    }

    const idsBeingDeleted = [...deleteTargetIds];

    const casesBeingDeleted = loancases.filter((loanCase) => {
      const id = loanCase.loan_case_id || loanCase.id;

      return idsBeingDeleted.includes(id);
    });

    try {
      setIsDeleting(true);
      setDeleteError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/lender/loan-cases/bulk-delete/`,
        {
          method: "DELETE",

          headers: {
            Authorization: `Bearer ${token}`,

            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            loan_case_ids: idsBeingDeleted,
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            data?.errors?.loan_case_ids?.[0] ||
            "The selected loan cases could not be deleted.",
        );
      }

      const deletedCount =
        Number(data?.deleted_count) || idsBeingDeleted.length;

      if (deletedCount === 1) {
        const deletedCase = casesBeingDeleted[0];

        const caseLabel =
          deletedCase?.case_reference ||
          deletedCase?.loan_case_reference ||
          idsBeingDeleted[0];

        showToast({
          type: "success",

          title: t("delete.toastSingleTitle", "Loan case deleted"),

          message: t("delete.toastSingleMessage", {
            caseReference: caseLabel,

            defaultValue:
              "Loan case {{caseReference}} was deleted successfully.",
          }),

          duration: 5000,
        });
      } else {
        showToast({
          type: "success",

          title: t("delete.toastMultipleTitle", "Loan cases deleted"),

          message: t("delete.toastMultipleMessage", {
            count: deletedCount,

            defaultValue: "{{count}} loan cases were deleted successfully.",
          }),

          duration: 5000,
        });
      }

      const deletedAllVisible = idsBeingDeleted.length >= loancases.length;

      setIsDeleteModalOpen(false);

      setDeleteTargetIds([]);

      setSelectedLoanCaseIds([]);

      setIsSelectionMode(false);

      if (deletedAllVisible && currentPage > 1) {
        setCurrentPage((previous) => previous - 1);
      } else {
        setReloadKey((previous) => previous + 1);
      }
    } catch (err) {
      const message =
        err?.message || "The selected loan cases could not be deleted.";

      setDeleteError(message);

      showToast({
        type: "error",

        title: t("delete.toastErrorTitle", "Deletion failed"),

        message,

        duration: 7000,
      });
    } finally {
      setIsDeleting(false);
    }
  }

  React.useEffect(() => {
    setSelectedLoanCaseIds([]);

    setIsSelectionMode(false);
  }, [currentPage, pageSize, searchTerm, sourceFilter, statusFilter]);

  function handleRefresh() {
    if (isRefreshing || isInitialLoading) {
      return;
    }

    setReloadKey((previous) => previous + 1);
  }

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="loan-cases-page">
      <div
        className={`loan-cases-page__content ${
          (isCreateModalOpen && !isCreateModalClosing) ||
          isDeleteModalOpen ||
          selectedRenovationPlan ||
          selectedRenovationOutcome
            ? "loan-cases-page__content--blurred"
            : ""
        }`}
      >
        {/* HERO */}

        <div className="loan-cases-page__hero">
          <h1 className="loan-cases-page__title">{t("hero.title")}</h1>

          <p className="loan-cases-page__subtitle">{t("hero.subtitle")}</p>
        </div>

        {/* TABS */}

        <div className="loan-cases-page__tabs">
          <button
            type="button"
            className={`loan-cases-page__tab ${
              sourceFilter === "REFERENCE_IMPORT"
                ? "loan-cases-page__tab--active"
                : ""
            }`}
            onClick={() => setSourceFilter("REFERENCE_IMPORT")}
          >
            {t("tabs.linked_cases")}
          </button>

          <button
            type="button"
            className={`loan-cases-page__tab ${
              sourceFilter === "MANUAL_ENTRY"
                ? "loan-cases-page__tab--active"
                : ""
            }`}
            onClick={() => setSourceFilter("MANUAL_ENTRY")}
          >
            {t("tabs.manual_entry")}
          </button>

          <button
            type="button"
            className={`loan-cases-page__tab ${
              sourceFilter === "all" ? "loan-cases-page__tab--active" : ""
            }`}
            onClick={() => setSourceFilter("all")}
          >
            {t("tabs.all_cases")}
          </button>
        </div>

        {/* TOOLBAR */}

        <div className="loan-cases-page__toolbar">
          <div className="loan-cases-page__filters">
            <label className="loan-cases-page__select-wrap">
              <select
                className="loan-cases-page__select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">{t("filters.status.all")}</option>

                <option value="OPEN">{t("filters.status.open")}</option>

                <option value="RESULT_RECORDED">
                  {t("filters.status.resultRecorded")}
                </option>
              </select>

              <ChevronDown size={16} className="loan-cases-page__select-icon" />
            </label>

            <label className="loan-cases-page__select-wrap loan-cases-page__select-wrap--small">
              <select
                className="loan-cases-page__select"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                <option value={6}>6 / {t("filters.page")}</option>

                <option value={10}>10 / {t("filters.page")}</option>

                <option value={12}>12 / {t("filters.page")}</option>
              </select>

              <ChevronDown size={16} className="loan-cases-page__select-icon" />
            </label>

            <label className="loan-cases-page__search">
              <Search size={16} className="loan-cases-page__search-icon" />

              <input
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={t("filters.searchPlaceholder")}
                className="loan-cases-page__search-input"
              />
            </label>
          </div>

          <div className="loan-cases-page__toolbar-right">
            <div className="loan-cases-page__toolbar-status">
              <div className="loan-cases-page__count-pill">
                {t("toolbar.totalLoancases", {
                  count: totalLoancases,
                })}
              </div>

              <button
                type="button"
                className="loan-cases-page__add-btn"
                onClick={() => {
                  setManualCreateError("");

                  if (marketRegions.length === 1) {
                    setSelectedMarketRegionId(String(marketRegions[0].id));
                  } else {
                    setSelectedMarketRegionId("");
                  }

                  setIsCreateModalOpen(true);
                }}
              >
                <Plus size={16} strokeWidth={2.5} />

                <span>{t("toolbar.createLoanCase")}</span>
              </button>

              <button
                type="button"
                className="loan-cases-page__refresh-btn"
                onClick={handleRefresh}
                disabled={isRefreshing || isInitialLoading}
                title={t("toolbar.refresh", "Refresh loan cases")}
                aria-label={t("toolbar.refresh", "Refresh loan cases")}
              >
                <RefreshCw
                  size={17}
                  strokeWidth={2.2}
                  className={
                    isRefreshing
                      ? "loan-cases-page__refresh-icon loan-cases-page__refresh-icon--spinning"
                      : "loan-cases-page__refresh-icon"
                  }
                />
              </button>
            </div>
          </div>
        </div>

        {/* RESULTS */}

        <section className="loan-cases-page__results-card">
          <div className="loan-cases-page__results-header">
            <div className="loan-cases-page__results-heading">
              <span className="loan-cases-page__results-label">
                {t("results.label")}
              </span>

              <span className="loan-cases-page__results-meta">
                {t("results.showing", {
                  start: startIndex,

                  end: endIndex,

                  total: totalLoancases,
                })}
              </span>
            </div>

            <div className="loan-cases-page__results-actions">
              {!isSelectionMode ? (
                <button
                  type="button"
                  className="loan-cases-page__select-mode-btn"
                  onClick={enterSelectionMode}
                  disabled={!visibleLoanCaseIds.length || isInitialLoading}
                >
                  <Check size={15} />

                  <span>{t("results.select", "Select")}</span>
                </button>
              ) : (
                <>
                  <label className="loan-cases-page__selection-checkbox">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleVisibleLoanCases}
                    />

                    <span>{t("results.selectPage", "Select page")}</span>
                  </label>

                  <span className="loan-cases-page__selected-count">
                    {t("results.selectedCount", {
                      count: selectedLoanCaseIds.length,

                      defaultValue: "{{count}} selected",
                    })}
                  </span>

                  <button
                    type="button"
                    className="loan-cases-page__delete-selected-btn"
                    onClick={() => openDeleteModal(selectedLoanCaseIds)}
                    disabled={!selectedLoanCaseIds.length}
                  >
                    <Trash2 size={15} />

                    <span>
                      {t("results.deleteSelected", "Delete selected")}
                    </span>
                  </button>

                  <button
                    type="button"
                    className="loan-cases-page__cancel-selection-btn"
                    onClick={exitSelectionMode}
                  >
                    <X size={15} />

                    <span>{t("results.cancelSelection", "Cancel")}</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="loan-cases-page__results-list">
            {isInitialLoading ? (
              <div className="loan-cases-page__skeleton-list">
                {Array.from({
                  length: pageSize,
                }).map((_, index) => (
                  <div
                    key={index}
                    className="loan-cases-page__row loan-cases-page__row--skeleton"
                  >
                    <div className="loan-cases-page__skeleton-avatar" />

                    <div className="loan-cases-page__skeleton-content">
                      <span className="loan-cases-page__skeleton-line loan-cases-page__skeleton-line--title" />
                      <span className="loan-cases-page__skeleton-line loan-cases-page__skeleton-line--details" />
                      <span className="loan-cases-page__skeleton-line loan-cases-page__skeleton-line--small" />
                    </div>

                    <div className="loan-cases-page__skeleton-right">
                      <span className="loan-cases-page__skeleton-line loan-cases-page__skeleton-line--amount" />
                      <span className="loan-cases-page__skeleton-line loan-cases-page__skeleton-line--button" />
                    </div>
                  </div>
                ))}
              </div>
            ) : showFullError ? (
              <div className="loan-cases-page__empty">
                <p className="loan-cases-page__empty-title">
                  {t("errors.title")}
                </p>

                <p className="loan-cases-page__empty-text">{error}</p>
              </div>
            ) : showEmpty ? (
              <div className="loan-cases-page__empty">
                <p className="loan-cases-page__empty-title">
                  {t("empty.title")}
                </p>

                <p className="loan-cases-page__empty-text">{t("empty.text")}</p>
              </div>
            ) : showRows ? (
              loancases.map((loanCase) => {
                const id = loanCase.loan_case_id || loanCase.id;

                const additionalLoans = getAdditionalLoans(loanCase);

                const availableRenovationPlan =
                  getAvailableRenovationPlan(loanCase);

                const externalRenovationAvailable =
                  hasExternalRenovation(loanCase);

                const isExternalRenovationPlan =
                  availableRenovationPlan?.source === "EXTERNAL_OSS";

                const additionalLoanTotal = getAdditionalLoanTotal(loanCase);

                const combinedLoanAmount = getCombinedLoanAmount(loanCase);

                const isSelected = selectedLoanCaseIds.includes(id);

                const hasTarget = Boolean(getLoanCaseTarget(loanCase));

                const isRowInteractive = isSelectionMode || hasTarget;

                function handleRowInteraction() {
                  if (isSelectionMode) {
                    toggleLoanCaseSelection(id);

                    return;
                  }

                  if (hasTarget) {
                    openLoanCase(loanCase);
                  }
                }

                return (
                  <article
                    key={id}
                    className={`loan-cases-page__row ${
                      !isSelectionMode && hasTarget
                        ? "loan-cases-page__row--clickable"
                        : "loan-cases-page__row--completed"
                    } ${
                      isSelectionMode
                        ? "loan-cases-page__row--selection-mode"
                        : ""
                    } ${isSelected ? "loan-cases-page__row--selected" : ""}`}
                    onClick={
                      isRowInteractive ? handleRowInteraction : undefined
                    }
                    role={isRowInteractive ? "button" : undefined}
                    tabIndex={isRowInteractive ? 0 : undefined}
                    onKeyDown={
                      isRowInteractive
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();

                              handleRowInteraction();
                            }
                          }
                        : undefined
                    }
                  >
                    {/* LEFT */}

                    <div className="loan-cases-page__row-left">
                      {isSelectionMode ? (
                        <label
                          className="loan-cases-page__row-checkbox"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleLoanCaseSelection(id)}
                          />
                        </label>
                      ) : null}

                      {isReferenceCase(loanCase) ? (
                        <ApplicantAvatar loanCase={loanCase} />
                      ) : null}

                      <div className="loan-cases-page__row-main">
                        <div className="loan-cases-page__row-topline">
                          <h3 className="loan-cases-page__code">
                            {loanCase.case_reference ||
                              loanCase.loan_case_reference}
                          </h3>

                          <span
                            className={`loan-cases-page__badge ${
                              loanCase.source === "MANUAL_ENTRY"
                                ? "loan-cases-page__badge--manual"
                                : "loan-cases-page__badge--reference"
                            }`}
                          >
                            {getSourceLabel(loanCase.source)}
                          </span>
                        </div>

                        <p className="loan-cases-page__details">
                          <strong>
                            {loanCase.primary_applicant_name || "—"}
                          </strong>

                          {loanCase.country ? ` • ${loanCase.country}` : ""}

                          {loanCase.market_region?.name
                            ? ` • ${loanCase.market_region.name}`
                            : ""}
                        </p>

                        <div className="loan-cases-page__case-meta">
                          {loanCase.case_type ? (
                            <span className="loan-cases-page__case-meta-item">
                              <Landmark size={13} />

                              {getCaseTypeLabel(loanCase.case_type)}
                            </span>
                          ) : null}

                          {loanCase.property_type ? (
                            <span className="loan-cases-page__case-meta-item">
                              <House size={13} />

                              {getPropertyTypeLabel(loanCase.property_type)}

                              {loanCase.size_sqm ? (
                                <span className="loan-cases-page__case-meta-size">
                                  {loanCase.size_sqm} m²
                                </span>
                              ) : null}
                            </span>
                          ) : null}
                        </div>

                        {/* RENOVATION PLAN */}

                        {/* RENOVATION PLAN */}

                        {canOpenDecision(loanCase) ? (
                          <div className="loan-cases-page__renovation-plan">
                            {availableRenovationPlan ? (
                              <button
                                type="button"
                                className={`loan-cases-page__renovation-plan-btn ${
                                  shouldReviewRenovationFinancing(loanCase)
                                    ? "loan-cases-page__renovation-plan-btn--review"
                                    : renovationFinancingIsConfirmed(loanCase)
                                      ? "loan-cases-page__renovation-plan-btn--confirmed"
                                      : ""
                                }`}
                                onClick={(event) => {
                                  event.stopPropagation();

                                  setSelectedRenovationPlan(
                                    availableRenovationPlan,
                                  );
                                }}
                              >
                                <FileCheck2 size={14} />

                                <span>
                                  {isExternalRenovationPlan
                                    ? shouldReviewRenovationFinancing(loanCase)
                                      ? t(
                                          "renovationPlan.externalAvailableReview",
                                          "External OSS renovation plan available — review financing",
                                        )
                                      : renovationFinancingIsConfirmed(loanCase)
                                        ? t(
                                            "renovationPlan.externalAvailableConfirmed",
                                            "External OSS renovation plan available — financing confirmed",
                                          )
                                        : t(
                                            "renovationPlan.externalAvailable",
                                            "External OSS renovation plan available",
                                          )
                                    : shouldReviewRenovationFinancing(loanCase)
                                      ? t(
                                          "renovationPlan.availableReview",
                                          "Renovation plan available — review financing",
                                        )
                                      : renovationFinancingIsConfirmed(loanCase)
                                        ? t(
                                            "renovationPlan.availableConfirmed",
                                            "Renovation plan available — financing confirmed",
                                          )
                                        : t(
                                            "renovationPlan.available",
                                            "Renovation plan available",
                                          )}
                                </span>
                              </button>
                            ) : externalRenovationAvailable ? (
                              <div className="loan-cases-page__renovation-plan-pending">
                                <FileClock size={14} />

                                <span>
                                  {t(
                                    "renovationPlan.externalPending",
                                    "External OSS · Renovation plan pending",
                                  )}
                                </span>
                              </div>
                            ) : renovationFinancingIsEstimated(loanCase) ? (
                              <div className="loan-cases-page__renovation-plan-pending">
                                <FileClock size={14} />

                                <span>
                                  {t(
                                    "renovationPlan.pending",
                                    "Renovation plan not available yet",
                                  )}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {/* RENOVATION OUTCOME */}

                        {canOpenDecision(loanCase) &&
                        externalRenovationAvailable ? (
                          <div className="loan-cases-page__renovation-outcome">
                            {externalRenovationIsCompleted(loanCase) &&
                            hasExternalFinalEpc(loanCase) ? (
                              <button
                                type="button"
                                className={
                                  "loan-cases-page__renovation-outcome-btn " +
                                  "loan-cases-page__renovation-outcome-btn--completed"
                                }
                                onClick={(event) => {
                                  event.stopPropagation();

                                  setSelectedRenovationOutcome(
                                    loanCase.external_renovation,
                                  );
                                }}
                              >
                                <FileCheck2 size={14} />

                                <span>
                                  {t("renovationOutcome.completed", {
                                    epc:
                                      loanCase?.external_renovation?.outcome
                                        ?.final_epc_label || "—",

                                    defaultValue:
                                      "Renovation completed · Final EPC {{epc}}",
                                  })}
                                </span>
                              </button>
                            ) : hasExternalRenovationOutcome(loanCase) ? (
                              <button
                                type="button"
                                className={
                                  "loan-cases-page__renovation-outcome-btn " +
                                  "loan-cases-page__renovation-outcome-btn--pending"
                                }
                                onClick={(event) => {
                                  event.stopPropagation();

                                  setSelectedRenovationOutcome(
                                    loanCase.external_renovation,
                                  );
                                }}
                              >
                                <FileClock size={14} />

                                <span>
                                  {hasExternalFinalEpc(loanCase)
                                    ? t(
                                        "renovationOutcome.available",
                                        "Renovation outcome available",
                                      )
                                    : t(
                                        "renovationOutcome.epcPending",
                                        "Renovation outcome available · Final EPC pending",
                                      )}
                                </span>
                              </button>
                            ) : (
                              <div className="loan-cases-page__renovation-outcome-pending">
                                <FileClock size={14} />

                                <span>
                                  {t(
                                    "renovationOutcome.pending",
                                    "Renovation outcome pending",
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : null}

                        {additionalLoans.length > 0 ? (
                          <div
                            className="loan-cases-page__additional-loans"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="loan-cases-page__additional-loans-header">
                              <div>
                                <Landmark size={14} />

                                <span>
                                  {t(
                                    "additionalLoans.title",
                                    "Additional renovation financing",
                                  )}
                                </span>
                              </div>

                              <strong>
                                +{formatCurrency(additionalLoanTotal)}
                              </strong>
                            </div>

                            <div className="loan-cases-page__additional-loans-list">
                              {additionalLoans.map((additionalLoan) => (
                                <div
                                  key={
                                    additionalLoan.loan_case_id ||
                                    additionalLoan.id ||
                                    additionalLoan.case_reference
                                  }
                                  className="loan-cases-page__additional-loan-item"
                                >
                                  <div className="loan-cases-page__additional-loan-copy">
                                    <span>
                                      {additionalLoan.case_reference ||
                                        t(
                                          "additionalLoans.fallbackReference",
                                          "Additional loan",
                                        )}
                                    </span>

                                    {additionalLoan.lender_external_reference ? (
                                      <small>
                                        {
                                          additionalLoan.lender_external_reference
                                        }
                                      </small>
                                    ) : null}
                                  </div>

                                  <div className="loan-cases-page__additional-loan-right">
                                    <strong>
                                      {formatCurrency(
                                        additionalLoan.approved_renovation_loan_amount ??
                                          additionalLoan.approved_total_loan_amount,
                                      )}
                                    </strong>

                                    <span className="loan-cases-page__additional-loan-status">
                                      {t(
                                        "additionalLoans.approved",
                                        "Approved",
                                      )}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        <p className="loan-cases-page__uploaded">
                          {t("row.uploaded")}: {loanCase.uploaded_label || "—"}
                        </p>
                      </div>
                    </div>

                    {/* RIGHT */}

                    <div className="loan-cases-page__row-right">
                      <div className="loan-cases-page__amount-group">
                        <div className="loan-cases-page__amount">
                          {formatCurrency(combinedLoanAmount)}
                        </div>

                        {additionalLoans.length > 0 ? (
                          <span className="loan-cases-page__amount-breakdown">
                            {formatCurrency(loanCase.loan_amount)}
                            {" + "}
                            {formatCurrency(additionalLoanTotal)}
                          </span>
                        ) : null}
                      </div>

                      <span
                        className={`loan-cases-page__decision-status ${
                          hasFinalDecision(loanCase)
                            ? "loan-cases-page__decision-status--completed"
                            : hasDraftDecision(loanCase)
                              ? "loan-cases-page__decision-status--draft"
                              : "loan-cases-page__decision-status--pending"
                        }`}
                      >
                        {getDecisionLabel(loanCase)}
                      </span>

                      {!isSelectionMode ? (
                        <div className="loan-cases-page__actions">
                          {canContinueManualEntry(loanCase) ? (
                            <button
                              type="button"
                              className="loan-cases-page__ghost-btn"
                              onClick={(event) => {
                                event.stopPropagation();

                                navigate(getManualEntryTarget(loanCase));
                              }}
                            >
                              {t("row.continueEntry")}
                            </button>
                          ) : null}

                          {canOpenDecision(loanCase) ? (
                            <button
                              type="button"
                              className={
                                hasExistingLenderDecision(loanCase)
                                  ? "loan-cases-page__secondary-btn"
                                  : "loan-cases-page__primary-btn"
                              }
                              onClick={(event) => {
                                event.stopPropagation();

                                recordLoanCaseResult(loanCase);
                              }}
                            >
                              {getDecisionActionLabel(loanCase)}
                            </button>
                          ) : null}

                          {canAddAdditionalLoan(loanCase) ? (
                            <button
                              type="button"
                              className="loan-cases-page__primary-btn"
                              onClick={(event) => {
                                event.stopPropagation();

                                navigate(
                                  `/lender/loan-cases/${id}/additional-loan`,
                                );
                              }}
                            >
                              <Plus size={14} />

                              <span>
                                {t("row.addAdditionalLoan", "Additional loan")}
                              </span>
                            </button>
                          ) : null}

                          <button
                            type="button"
                            className="loan-cases-page__delete-row-btn"
                            onClick={(event) => {
                              event.stopPropagation();

                              openDeleteModal([id]);
                            }}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ) : (
                        <div
                          className={`loan-cases-page__selection-state ${
                            isSelected
                              ? "loan-cases-page__selection-state--selected"
                              : ""
                          }`}
                        >
                          <Check size={14} />

                          <span>
                            {isSelected
                              ? t("results.selected", "Selected")
                              : t("results.selectCase", "Select")}
                          </span>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })
            ) : null}
          </div>

          {/* PAGINATION */}

          <div className="loan-cases-page__pagination">
            <div className="loan-cases-page__pagination-left">
              <button
                type="button"
                className="loan-cases-page__page-nav"
                onClick={() =>
                  setCurrentPage((previous) => Math.max(1, previous - 1))
                }
                disabled={currentPage === 1 || isInitialLoading}
              >
                <ChevronLeft size={16} />

                {t("pagination.prev")}
              </button>

              <button
                type="button"
                className="loan-cases-page__page-nav"
                onClick={() =>
                  setCurrentPage((previous) =>
                    Math.min(totalPages, previous + 1),
                  )
                }
                disabled={currentPage === totalPages || isInitialLoading}
              >
                {t("pagination.next")}

                <ChevronRight size={16} />
              </button>
            </div>

            <div className="loan-cases-page__page-numbers">
              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`loan-cases-page__page-number ${
                    page === currentPage
                      ? "loan-cases-page__page-number--active"
                      : ""
                  }`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <div className="loan-cases-page__pagination-right">
              {t("pagination.pageXofY", {
                page: currentPage,

                total: totalPages,
              })}
            </div>
          </div>
        </section>
      </div>

      {/* CREATE */}

      {isCreateModalOpen ? (
        <CreateLoanCaseModal
          isClosing={isCreateModalClosing}
          isCreatingManual={isCreatingManual}
          manualCreateError={manualCreateError}
          marketRegions={marketRegions}
          selectedMarketRegionId={selectedMarketRegionId}
          onMarketRegionChange={setSelectedMarketRegionId}
          onClose={() => {
            if (isCreatingManual) {
              return;
            }

            setManualCreateError("");
            setSelectedMarketRegionId("");

            closeCreateModal();
          }}
          onCreateManual={handleCreateManualLoanCase}
          onLinkedCreated={() => {
            closeCreateModal();

            setSelectedMarketRegionId("");

            setSourceFilter("all");
            setStatusFilter("all");
            setSearchInput("");
            setSearchTerm("");
            setCurrentPage(1);

            window.setTimeout(() => {
              setReloadKey((previous) => previous + 1);
            }, 220);
          }}
        />
      ) : null}

      {/* DELETE */}

      {isDeleteModalOpen ? (
        <div
          className="loan-cases-page__delete-backdrop"
          onMouseDown={closeDeleteModal}
        >
          <div
            className="loan-cases-page__delete-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="loan-cases-page__delete-icon">
              <Trash2 size={22} />
            </div>

            <h2 className="loan-cases-page__delete-title">
              {deleteTargetIds.length === 1
                ? t("delete.titleSingle", "Delete loan case?")
                : t("delete.titleMultiple", "Delete loan cases?")}
            </h2>

            <p className="loan-cases-page__delete-text">
              {t(
                "delete.warning",
                "This permanently deletes the selected loan case data from the database. This action cannot be undone.",
              )}
            </p>

            {deleteError ? (
              <div className="loan-cases-page__delete-error">{deleteError}</div>
            ) : null}

            <div className="loan-cases-page__delete-actions">
              <button
                type="button"
                className="loan-cases-page__delete-cancel"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                {t("delete.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="loan-cases-page__delete-confirm"
                onClick={confirmDeleteLoanCases}
                disabled={isDeleting}
              >
                {isDeleting
                  ? t("delete.deleting", "Deleting...")
                  : t("delete.confirm", "Delete permanently")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* RENOVATION PLAN */}

      {selectedRenovationPlan ? (
        <RenovationPlanViewerModal
          plan={selectedRenovationPlan}
          onClose={() => setSelectedRenovationPlan(null)}
        />
      ) : null}

      {/* RENOVATION OUTCOME */}

      {selectedRenovationOutcome ? (
        <RenovationOutcomeModal
          renovation={selectedRenovationOutcome}
          onClose={() => setSelectedRenovationOutcome(null)}
        />
      ) : null}
    </div>
  );
}
